import React, { useState, useContext } from "react";
import classnames from "classnames";
import Link from "next/link";
import Styles from "./ExploreJobs.module.scss";
import ResponsiveContainer from "@vahak/core/dist/components/responsive-container/ResponsiveContainer";
import JobCard from "./job-card/JobCard";
import AppBtn from "@vahak/core/dist/components/app-btn/AppBtn";
import { CareersService, Categ, JobsResponse } from "@vahak/core/dist/_services/careers.service";
import { AppContext } from "@vahak/core/dist/app-context";
import A from "@vahak/core/dist/components/app-anchor/A";
import { sleep } from "@vahak/core/dist/methods/sleep";
import ArrowDownBlue from "@vahak/core/dist/icons/ArrowDownBlue.svg";

import QuickFilterPill from "@vahak/core/dist/components/quick-filter-pill/QuickFilterPill";
const MAX_JOBS_PER_PAGE = 6;

const AllCateg: Categ = { name: "All Jobs", slug: "all" };
const AllCategMobile: Categ = { name: "All Job Openings", slug: "all" };

interface GetSetDataParams {
    categ: Categ;
    page: number;
}

interface ExploreJobsProps {
    jobsData: JobsResponse["jobs"] | null;
    meta: any;
    categories: Categ[];
}

const ExploreJobs = ({ jobsData, meta, categories }: ExploreJobsProps) => {
    const { isMobile } = useContext(AppContext);

    /***
     * states
     */
    const [categs, setCategs] = useState<Categ[]>([isMobile ? AllCategMobile : AllCateg, ...(categories || [])]);
    const [selectedCateg, setSelectedCateg] = useState<Categ>(isMobile ? AllCategMobile : AllCateg);
    const [jobs, setJobs] = useState<JobsResponse["jobs"] | null>(jobsData);
    const [totalJobs, setTotalJobs] = useState(meta?.pagination.total || 0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalsPages] = useState(meta?.pagination.pages || 1);
    const [isLoading, setIsLoading] = useState(false);

    const onClickShowMore = async () => {
        const t1 = Date.now();
        setPage((state) => state + 1);
        // getAndSetData({ page: page + 1, categ: selectedCateg });
        setIsLoading(true);
        if (selectedCateg.slug.includes("all")) {
            const { jobs, meta } = await CareersService.getAllJobs({ page: page + 1, limit: MAX_JOBS_PER_PAGE });
            setJobs((state) => [...(state || []), ...(jobs || [])]);
            setTotalsPages(meta?.pagination.pages || 1);
            setTotalJobs(meta?.pagination.total || 0);
        } else {
            const { jobs, meta } = await CareersService.getJobsByCateg({
                page: page + 1,
                limit: MAX_JOBS_PER_PAGE,
                categSlug: selectedCateg.slug
            });
            setJobs((state) => [...(state || []), ...(jobs || [])]);
            setTotalsPages(meta?.pagination.pages || 1);
            setTotalJobs(meta?.pagination.total || 0);
        }
        const t2 = Date.now();
        if (t2 - t2 < 500) {
            await sleep(500 - (t2 - t1));
        }
        setIsLoading(false);
    };

    // useEffect(() => {
    //     isLoading && setJobs(getEmptyArray(jobs?.length || 0));
    // }, [isLoading]);

    const handleOnSelectCategory = async (cateSlug: any) => {
        setSelectedCateg(categs.find((c) => c.slug === cateSlug) || AllCategMobile);
        if (cateSlug === "all") {
            const { jobs, meta } = await CareersService.getAllJobs({ page, limit: MAX_JOBS_PER_PAGE });
            setJobs(jobs);
            setTotalsPages(meta?.pagination.pages || 1);
            setTotalJobs(meta?.pagination.total || 0);
        } else {
            const { jobs, meta } = await CareersService.getJobsByCateg({
                page: 1,
                limit: MAX_JOBS_PER_PAGE,
                categSlug: cateSlug
            });
            setJobs(jobs);
            setTotalsPages(meta?.pagination.pages || 1);
            setTotalJobs(meta?.pagination.total || 0);
        }
    };

    return (
        <>
            <div className={Styles.explorejobsHeader}>
                <h2>Open Positions</h2>
            </div>

            <div className={Styles.categoryButtonsWrap}>
                {isMobile ? (
                    <div className={Styles.jobDropDownBox}>
                        <select
                            onChange={(e) => {
                                e.persist();
                                handleOnSelectCategory(
                                    categs.find((c) => c.slug === e.target.value)?.slug || AllCategMobile
                                );
                            }}
                        >
                            {categs.map(({ name, slug }) => (
                                <option key={slug} value={slug}>
                                    {name}
                                </option>
                            ))}
                        </select>
                        <span className={Styles.iconDownArow}>
                            <ArrowDownBlue />
                        </span>
                    </div>
                ) : (
                    <ResponsiveContainer>
                        {categs.map(({ name, slug }) => (
                            <QuickFilterPill
                                className={Styles.categoryButton}
                                key={slug}
                                text={name}
                                data={{ name, slug }}
                                isSelected={selectedCateg.slug === slug}
                                selectedClassName={Styles.selectedCateg}
                                onSelected={() => handleOnSelectCategory(slug)}
                            />
                        ))}
                    </ResponsiveContainer>
                )}
            </div>

            <div className={Styles.jobCardWrap}>
                <h3>
                    {jobs && jobs?.length > 0 && (
                        <>
                            Showing {selectedCateg.slug === "all" ? "All" : selectedCateg.name} jobs ({totalJobs})
                        </>
                    )}
                </h3>
                <ResponsiveContainer>
                    {jobs?.map((job, i) => (
                        <Link
                            href={`/careers/${job?.slug}`}
                            key={job?.slug || i}
                            prefetch={false}
                            // onClick={() => handleJobDetails(job?.slug)}
                        >
                            <a
                                style={{
                                    margin: isMobile ? "7px 0" : "15px 0",
                                    padding: isMobile ? "0" : "0 15px",
                                    width: isMobile ? "100%" : "50%"
                                }}
                            >
                                <JobCard
                                    jobDetails={job}
                                    isLoading={isLoading && i + 1 > (page - 1) * MAX_JOBS_PER_PAGE}
                                />
                            </a>
                        </Link>
                    ))}
                </ResponsiveContainer>
                {page < totalPages && (
                    <div className={classnames(Styles.showMore)}>
                        <A onClick={onClickShowMore}>Show more</A>
                    </div>
                )}
            </div>

            <div className={Styles.couldntFindMatch}>
                <div className={Styles.findMatchRow}>
                    <h3>Can’t find your fit? Drop your CV and we’ll reach out when an opportunity comes up!</h3>
                    <A href="https://docs.google.com/forms/d/e/1FAIpQLSdIv1JUon9yquV5zVAwgJ4PoCAibeJtxSVWNNrQ4qlDP0dfxQ/viewform?usp=sf_link">
                        <AppBtn
                            disabled={false}
                            className={Styles.sendYourCV}
                            type="primary"
                            btnType="submit"
                            style={{
                                backgroundColor: "#FFFFFF",
                                color: "#08123B",
                                textAlign: "center",
                                fontSize: isMobile ? "14px" : "20px",
                                padding: isMobile ? "10px" : "15px",
                                height: isMobile ? "46px" : "64px",
                                width: isMobile ? "180px" : "254px",
                                display: "block",
                                marginTop: "10px",
                                marginBottom: "20px",
                                marginLeft: isMobile ? "0" : "auto",
                                marginRight: isMobile ? "auto" : "0",
                                lineHeight: "25px"
                            }}
                        >
                            <span>Drop your CV</span>
                        </AppBtn>
                    </A>
                </div>
            </div>
        </>
    );
};

export default ExploreJobs;
